/**
 * Shared Judge.me store review summary (public Widgets API).
 * Used by theme blocks that show store-wide average rating + count.
 *
 * Security: use your **public** API token from Judge.me only (never a private token in theme code).
 * @see https://judge.me/api/docs
 */
(function () {
  if (window.__SkJudgeMeStoreReviewsApi) return;

  const CACHE_KEY_PREFIX = 'sk_jm_store_v2_';

  function cacheKey(shopDomain) {
    return CACHE_KEY_PREFIX + shopDomain;
  }

  function getInflightStore() {
    if (!window.__skJudgeMeStoreReviewsInflight) {
      window.__skJudgeMeStoreReviewsInflight = Object.create(null);
    }
    return window.__skJudgeMeStoreReviewsInflight;
  }

  function readCache(shopDomain, cacheHours) {
    try {
      const raw = localStorage.getItem(cacheKey(shopDomain));
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || !parsed.ts) return null;
      const maxAgeMs = (Number(cacheHours) || 12) * 60 * 60 * 1000;
      if (Date.now() - parsed.ts > maxAgeMs) return null;
      if (typeof parsed.rating !== 'number' || typeof parsed.count !== 'number') return null;
      return parsed;
    } catch (e) {
      return null;
    }
  }

  function writeCache(shopDomain, rating, count) {
    try {
      localStorage.setItem(cacheKey(shopDomain), JSON.stringify({ ts: Date.now(), rating, count }));
    } catch (e) {}
  }

  function toNumber(value) {
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    if (typeof value === 'string') {
      const cleaned = value.trim().replace(/,/g, '');
      if (cleaned === '') return null;
      const parsed = Number(cleaned);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  }

  function pickNumber(data, keys) {
    for (const key of keys) {
      const value = key.split('.').reduce((obj, part) => obj && obj[part], data);
      const parsed = toNumber(value);
      if (parsed !== null) return parsed;
    }
    return toNumber(data);
  }

  async function fetchJson(url) {
    const res = await fetch(url, { credentials: 'omit', mode: 'cors' });
    if (!res.ok) throw new Error(`Judge.me fetch failed (${res.status})`);
    const text = await res.text();
    try {
      return JSON.parse(text);
    } catch (e) {
      const n = toNumber(text);
      if (n !== null) return n;
      throw new Error('Judge.me response was not JSON');
    }
  }

  async function fetchPrimaryPair(base, query) {
    const ratingData = await fetchJson(`${base}/all_reviews_rating?${query}`);
    const countData = await fetchJson(`${base}/all_reviews_count?${query}`);
    const rating = pickNumber(ratingData, [
      'all_reviews_rating',
      'average_rating',
      'avg_rating',
      'rating',
      'shop_reviews_rating',
      'shop_average_rating',
      'widget.all_reviews_rating',
      'data.all_reviews_rating',
      'data.rating',
      'response.all_reviews_rating',
    ]);
    const count = pickNumber(countData, [
      'all_reviews_count',
      'review_count',
      'reviews_count',
      'total_reviews',
      'count',
      'shop_reviews_count',
      'widget.all_reviews_count',
      'data.all_reviews_count',
      'data.count',
      'response.all_reviews_count',
    ]);
    return { rating, count };
  }

  /**
   * @param {string} shopDomain
   * @param {string} token Public Judge.me API token
   * @param {number} cacheHours
   * @returns {Promise<{rating:number,count:number}|null>}
   */
  async function fetchStoreReviews(shopDomain, token, cacheHours) {
    const inflightStore = getInflightStore();
    const inflightKey = cacheKey(shopDomain);
    if (inflightStore[inflightKey]) {
      return inflightStore[inflightKey];
    }

    inflightStore[inflightKey] = (async () => {
      const cached = readCache(shopDomain, cacheHours);
      if (cached) {
        return { rating: cached.rating, count: cached.count };
      }

      const query = `api_token=${encodeURIComponent(token)}&shop_domain=${encodeURIComponent(shopDomain)}`;
      const bases = ['https://api.judge.me/api/v1/widgets', 'https://judge.me/api/v1/widgets'];

      let rating = null;
      let count = null;
      let lastBase = bases[0];

      for (const base of bases) {
        try {
          lastBase = base;
          const pair = await fetchPrimaryPair(base, query);
          rating = pair.rating;
          count = pair.count;
          if (rating !== null && count !== null) break;
        } catch (e) {
          rating = null;
          count = null;
        }
      }

      if (rating === null || count === null) {
        try {
          const legacyData = await fetchJson(`${lastBase}/product_review?${query}&external_id=shop`);
          rating =
            toNumber(rating) ??
            pickNumber(legacyData, [
              'rating',
              'average_rating',
              'stats.average_rating',
              'shop.average_rating',
              'shop.rating',
              'data.rating',
            ]);
          count =
            toNumber(count) ??
            pickNumber(legacyData, [
              'reviewNumber',
              'count',
              'review_count',
              'stats.review_count',
              'shop.review_count',
              'shop.reviews_count',
              'data.review_count',
            ]);
        } catch (e) {
          /* legacy endpoint often 404; ignore */
        }
      }

      if (typeof rating === 'number' && typeof count === 'number') {
        writeCache(shopDomain, rating, count);
        return { rating, count };
      }
      return null;
    })();

    try {
      return await inflightStore[inflightKey];
    } finally {
      delete inflightStore[inflightKey];
    }
  }

  /**
   * Store-wide Judge.me rating widgets: any root with [data-bcc-rating],
   * optional token on data-reviews-token, star colours via data-star-*.
   */
  function initBccStoreRatingWidgets() {
    document.querySelectorAll('[data-bcc-rating]').forEach((widget) => {
      if (widget.getAttribute('data-bcc-rating-init') === '1') return;

      const shopDomain = widget.getAttribute('data-shop-domain');
      const token = (widget.getAttribute('data-reviews-token') || '')
        .replace(/[\u200B-\u200D\uFEFF]/g, '')
        .trim();
      const cacheHours = Number(widget.getAttribute('data-cache-hours')) || 12;
      const valueEl = widget.querySelector('[data-bcc-rating-value]');
      const countEl = widget.querySelector('[data-bcc-rating-count]');
      const starsEl = widget.querySelector('[data-bcc-rating-stars]');
      if (!valueEl || !countEl || !starsEl) return;

      widget.setAttribute('data-bcc-rating-init', '1');

      const fullClass =
        widget.getAttribute('data-star-full-class') || 'bcc-hero__star bcc-hero__star--full';
      const emptyClass =
        widget.getAttribute('data-star-empty-class') || 'bcc-hero__star bcc-hero__star--empty';
      const fillFull = widget.getAttribute('data-star-fill-full') || '#1a1a1a';
      const fillEmpty = widget.getAttribute('data-star-fill-empty') || '#1a1a1a';
      const emptyOpacity = widget.getAttribute('data-star-empty-opacity') || '0.35';

      function formatCount(n) {
        return Number(n).toLocaleString();
      }

      function renderStars(rating) {
        const rounded = Math.floor(Number(rating) + 0.5);
        let out = '';
        const fullSvg = `<svg class="${fullClass}" viewBox="0 0 20 20" width="18" height="18" aria-hidden="true"><path fill="${fillFull}" d="M10 15.273l-5.878 3.09 1.123-6.545L.49 7.273l6.573-.955L10 .364l2.938 5.954 6.572.955-4.755 4.545 1.122 6.545z"/></svg>`;
        const emptySvg = `<svg class="${emptyClass}" viewBox="0 0 20 20" width="18" height="18" aria-hidden="true"><path fill="${fillEmpty}" fill-opacity="${emptyOpacity}" d="M10 15.273l-5.878 3.09 1.123-6.545L.49 7.273l6.573-.955L10 .364l2.938 5.954 6.572.955-4.755 4.545 1.122 6.545z"/></svg>`;
        for (let i = 1; i <= 5; i++) {
          out += i <= rounded ? fullSvg : emptySvg;
        }
        starsEl.innerHTML = out;
      }

      function applyTemplate(template, rating, count) {
        return template
          .replace(/\{rating\}/g, rating.toFixed(2))
          .replace(/\{count\}/g, formatCount(count));
      }

      function update(rating, count) {
        const r = Number(rating);
        const c = Number(count);
        if (!Number.isFinite(r) || r <= 0 || !Number.isFinite(c) || c <= 0) return;
        valueEl.textContent = r.toFixed(2);
        const countTemplate = widget.getAttribute('data-bcc-rating-count-template');
        if (countTemplate) {
          countEl.textContent = applyTemplate(countTemplate, r, c);
        } else {
          countEl.textContent = `${formatCount(c)} from all store reviews`;
        }
        const ariaTemplate = widget.getAttribute('data-bcc-rating-aria-template');
        widget.setAttribute(
          'aria-label',
          ariaTemplate
            ? applyTemplate(ariaTemplate, r, c)
            : `Rated ${r.toFixed(2)} out of 5 from ${formatCount(c)} store reviews`,
        );
        renderStars(r);
      }

      async function waitForApi() {
        const start = Date.now();
        while (Date.now() - start < 2500) {
          const api = window.__SkJudgeMeStoreReviewsApi;
          if (api && typeof api.fetchStoreReviews === 'function') return api;
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
        return null;
      }

      if (!shopDomain || !token) return;
      if (widget.getAttribute('data-judgeme-api-disabled') === 'true') return;

      (async () => {
        try {
          const api = await waitForApi();
          if (!api) return;
          const result = await api.fetchStoreReviews(shopDomain, token, cacheHours);
          if (result && typeof result.rating === 'number' && typeof result.count === 'number') {
            update(result.rating, result.count);
          }
        } catch (e) {
          /* keep Liquid fallbacks */
        }
      })();
    });
  }

  function hideProductBadgesWhenStoreRatingPresent() {
    var hasStoreRating =
      document.querySelector('.product-details .pdp-store-rating') ||
      document.body.classList.contains('pdp-store-rating-line');
    if (!hasStoreRating) return;
    document
      .querySelectorAll(
        '.product-details .jdgm-preview-badge, .product-details .jdgm-widget.jdgm-preview-badge, .product-details .jdgm-prev-badge',
      )
      .forEach(function (el) {
        el.style.setProperty('display', 'none', 'important');
        el.setAttribute('hidden', '');
        el.setAttribute('aria-hidden', 'true');
        const wrap = el.closest('.shopify-block, .shopify-app-block');
        if (wrap) {
          wrap.style.setProperty('display', 'none', 'important');
          wrap.setAttribute('hidden', '');
          wrap.setAttribute('aria-hidden', 'true');
        }
      });
  }

  window.__SkJudgeMeStoreReviewsApi = {
    fetchStoreReviews,
    cacheKey,
    initBccStoreRatingWidgets,
  };

  initBccStoreRatingWidgets();
  hideProductBadgesWhenStoreRatingPresent();
  if (typeof MutationObserver !== 'undefined') {
    var badgeHidePending = false;
    var badgeObserver = new MutationObserver(function () {
      if (badgeHidePending) return;
      badgeHidePending = true;
      requestAnimationFrame(function () {
        badgeHidePending = false;
        hideProductBadgesWhenStoreRatingPresent();
      });
    });
    badgeObserver.observe(document.documentElement, { childList: true, subtree: true });
  }
})();
